import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Progress,
	ResponsiveTable,
	Skeleton,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@ancatag/at-design";
import {
	Activity,
	AlertCircle,
	ArrowRight,
	Image,
	Key,
	MessageSquare,
	Plus,
	TrendingUp,
	Video,
	Volume2,
	Zap,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";
import { CreateProjectDialog } from "../components/projects/CreateProjectDialog";
import { ProjectEmptyState } from "../components/projects/ProjectEmptyState";
import { useAuth } from "../context/AuthContext";
import { useProjectContext } from "../context/ProjectContext";
import { useDashboardStats } from "../hooks/admin";
import { useRealTimeStats } from "../hooks/useAnalytics";
import { useAllApiKeyMetrics, useApiKeys } from "../hooks/useApiKeys";
import type { GenerationType } from "../types/api-keys";

const typeIcons: Record<
	GenerationType,
	React.ComponentType<{ className?: string }>
> = {
	text: MessageSquare,
	image: Image,
	video: Video,
	audio: Volume2,
};

const typeColors: Record<GenerationType, string> = {
	text: "text-blue-600 bg-blue-500/10",
	image: "text-purple-600 bg-purple-500/10",
	video: "text-green-600 bg-green-500/10",
	audio: "text-orange-600 bg-orange-500/10",
};

export function Dashboard(): React.JSX.Element {
	const { user } = useAuth();
	const {
		selectedProject,
		isLoading: projectLoading,
		refreshProjects,
	} = useProjectContext();
	const [createProjectOpen, setCreateProjectOpen] = useState(false);
	const { data: apiKeys, isLoading: keysLoading } = useApiKeys();
	const { data: metrics, isLoading: metricsLoading } = useAllApiKeyMetrics();
	const { data: adminStats, isLoading: adminLoading } = useDashboardStats();
	const { data: realTimeStats } = useRealTimeStats();

	const isAdmin = user?.role === "admin" || user?.role === "superAdmin";
	const isLoading =
		projectLoading ||
		keysLoading ||
		metricsLoading ||
		(isAdmin && adminLoading);

	// Calculate usage by generation type from API keys (must be before early returns)
	const usageByType = useMemo(() => {
		const typeCounts: Record<GenerationType, number> = {
			text: 0,
			image: 0,
			video: 0,
			audio: 0,
		};

		apiKeys?.forEach((key) => {
			if (key.allowedGenerationTypes && key.allowedGenerationTypes.length > 0) {
				key.allowedGenerationTypes.forEach((type) => {
					typeCounts[type] += key.totalRequests || 0;
				});
			} else {
				// If no restrictions, count as "all types" - distribute evenly
				const requests = key.totalRequests || 0;
				typeCounts.text += requests;
				typeCounts.image += requests;
				typeCounts.video += requests;
				typeCounts.audio += requests;
			}
		});

		const total = Object.values(typeCounts).reduce((sum, val) => sum + val, 0);
		return {
			counts: typeCounts,
			total,
			percentages: Object.fromEntries(
				Object.entries(typeCounts).map(([type, count]) => [
					type,
					total > 0 ? (count / total) * 100 : 0,
				]),
			) as Record<GenerationType, number>,
		};
	}, [apiKeys]);

	const activeKeys = apiKeys?.filter((k) => k?.isActive).length || 0;
	const totalRequests =
		apiKeys?.reduce(
			(sum, k) =>
				sum + (typeof k?.totalRequests === "number" ? k.totalRequests : 0),
			0,
		) || 0;

	if (projectLoading) {
		return <DashboardSkeleton isAdmin={isAdmin} />;
	}

	if (!selectedProject) {
		return (
			<div className="space-y-6">
				<div className="flex items-center justify-between">
					<div>
						<h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
						<p className="text-muted-foreground mt-2">
							Select or create a project to view usage.
						</p>
					</div>
				</div>
				<ProjectEmptyState
					onCreateProject={() => setCreateProjectOpen(true)}
					onRefresh={refreshProjects}
				/>
				<CreateProjectDialog
					open={createProjectOpen}
					onOpenChange={setCreateProjectOpen}
				/>
			</div>
		);
	}

	if (isLoading) {
		return <DashboardSkeleton isAdmin={isAdmin} />;
	}

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
				<div>
					<h1 className="text-2xl md:text-3xl font-bold text-foreground">
						Welcome back{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
					</h1>
					<p className="text-muted-foreground mt-2">
						Here's an overview of your Nova AI usage
					</p>
				</div>
				<Link href="/api-keys">
					<Button className="w-full md:w-auto">
						<Plus className="w-4 h-4 mr-2" />
						Create API Key
					</Button>
				</Link>
			</div>

			{/* Quick Stats */}
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<Key className="w-4 h-4" />
							API Keys
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{activeKeys}</div>
						<p className="text-xs text-muted-foreground">
							{apiKeys?.length || 0} total, {activeKeys} active
						</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<Zap className="w-4 h-4" />
							Total Requests
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{totalRequests.toLocaleString()}
						</div>
						<p className="text-xs text-muted-foreground">All time usage</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<Activity className="w-4 h-4" />
							Success Rate
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-green-600">
							{metrics?.summary?.totalRequests
								? (
										((metrics.summary.totalRequests -
											(metrics.summary.errors || 0)) /
											metrics.summary.totalRequests) *
										100
									).toFixed(1)
								: "100"}
							%
						</div>
						<p className="text-xs text-muted-foreground">Last 30 days</p>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<TrendingUp className="w-4 h-4" />
							Cache Hit Rate
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{metrics?.summary?.cacheHitRate?.toFixed(1) || "0.0"}%
						</div>
						<p className="text-xs text-muted-foreground">This month</p>
					</CardContent>
				</Card>
			</div>

			{/* Quick Analytics Widget */}
			{metrics && (
				<Card>
					<CardHeader className="flex flex-row items-center justify-between">
						<CardTitle className="flex items-center gap-2">
							<Activity className="w-5 h-5" />
							Quick Analytics
						</CardTitle>
						<Link href="/analytics">
							<Button variant="ghost" size="sm" className="gap-1">
								View Full Analytics <ArrowRight className="w-4 h-4" />
							</Button>
						</Link>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
							<div className="space-y-1">
								<div className="text-sm text-muted-foreground">
									Requests Today
								</div>
								<div className="text-2xl font-bold">
									{realTimeStats?.totalRequests?.toLocaleString() || "0"}
								</div>
								<div className="text-xs text-muted-foreground">
									Last 5 minutes: {realTimeStats?.totalRequests || 0}
								</div>
							</div>
							<div className="space-y-1">
								<div className="text-sm text-muted-foreground">
									Cache Hit Rate
								</div>
								<div className="text-2xl font-bold">
									{metrics.summary?.cacheHitRate?.toFixed(1) || "0.0"}%
								</div>
								<div className="text-xs text-muted-foreground">
									{metrics.summary?.cacheHits || 0} hits out of{" "}
									{metrics.summary?.totalRequests || 0} requests
								</div>
							</div>
							<div className="space-y-1">
								<div className="text-sm text-muted-foreground">
									Average Latency
								</div>
								<div className="text-2xl font-bold">
									{metrics.summary?.avgLatencyMs
										? `${metrics.summary.avgLatencyMs.toFixed(0)} ms`
										: realTimeStats?.avgLatencyMs
											? `${realTimeStats.avgLatencyMs.toFixed(0)} ms`
											: "—"}
								</div>
								<div className="text-xs text-muted-foreground">
									{realTimeStats?.avgLatencyMs
										? `Current: ${realTimeStats.avgLatencyMs.toFixed(0)}ms`
										: "No recent data"}
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Main Content Grid */}
			<div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-8">
				{/* API Keys Overview */}
				<Card>
					<CardHeader className="flex flex-row items-center justify-between">
						<CardTitle className="flex items-center gap-2">
							<Key className="w-5 h-5" />
							Your API Keys
						</CardTitle>
						<Link href="/api-keys">
							<Button variant="ghost" size="sm" className="gap-1">
								View All <ArrowRight className="w-4 h-4" />
							</Button>
						</Link>
					</CardHeader>
					<CardContent>
						{apiKeys && apiKeys.length > 0 ? (
							<div className="flex gap-4 md:gap-6 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide">
								{apiKeys.slice(0, 3).map((key) =>
									key ? (
										<Link key={key.id} href={`/api-keys/${key.id}`}>
											<div className="flex items-center justify-between p-4 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer min-w-[280px] snap-start">
												<div className="space-y-1">
													<div className="flex items-center gap-2">
														<span className="font-medium">{key.name}</span>
														<Badge
															variant={key.isActive ? "default" : "secondary"}
															className={key.isActive ? "bg-green-500" : ""}
														>
															{key.isActive ? "Active" : "Inactive"}
														</Badge>
													</div>
													<div className="flex gap-1">
														{key.allowedGenerationTypes?.length ? (
															key.allowedGenerationTypes.map((type) => (
																<Badge
																	key={type}
																	variant="outline"
																	className={`text-xs ${typeColors[type]}`}
																>
																	{type}
																</Badge>
															))
														) : (
															<Badge variant="outline" className="text-xs">
																All
															</Badge>
														)}
													</div>
												</div>
												<div className="text-right">
													<div className="text-sm font-medium">
														{(typeof key.totalRequests === "number"
															? key.totalRequests
															: 0
														).toLocaleString()}
													</div>
													<div className="text-xs text-muted-foreground">
														requests
													</div>
												</div>
											</div>
										</Link>
									) : null,
								)}
								{apiKeys.length > 3 && (
									<p className="text-sm text-muted-foreground text-center">
										+{apiKeys.length - 3} more keys
									</p>
								)}
							</div>
						) : (
							<div className="text-center py-8">
								<Key className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
								<h3 className="font-semibold mb-2">No API Keys Yet</h3>
								<p className="text-sm text-muted-foreground mb-4">
									Create your first API key to start using Nova AI
								</p>
								<Link href="/api-keys">
									<Button>
										<Plus className="w-4 h-4 mr-2" />
										Create API Key
									</Button>
								</Link>
							</div>
						)}
					</CardContent>
				</Card>

				{/* Usage by Type */}
				<Card className="min-h-[360px]">
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Activity className="w-5 h-5" />
							Usage by Type
						</CardTitle>
					</CardHeader>
					<CardContent>
						<Tabs defaultValue="overview" className="w-full">
							<TabsList className="grid w-full grid-cols-2">
								<TabsTrigger value="overview">Overview</TabsTrigger>
								<TabsTrigger value="endpoints">By Endpoint</TabsTrigger>
							</TabsList>
							<TabsContent value="overview" className="space-y-4 mt-4">
								{(["text", "image", "video", "audio"] as GenerationType[]).map(
									(type) => {
										const Icon = typeIcons[type];
										const percentage = usageByType.percentages[type] || 0;
										const count = usageByType.counts[type] || 0;
										return (
											<div key={type} className="space-y-2">
												<div className="flex items-center justify-between text-sm">
													<div
														className={`flex items-center gap-2 ${typeColors[type]}`}
													>
														<Icon className="w-4 h-4" />
														<span className="capitalize font-medium">
															{type}
														</span>
													</div>
													<span className="text-muted-foreground">
														{percentage > 0
															? `${percentage.toFixed(1)}%`
															: "0%"}
														{count > 0 && (
															<span className="ml-2 text-xs">
																({count.toLocaleString()})
															</span>
														)}
													</span>
												</div>
												<Progress value={percentage} className="h-2" />
											</div>
										);
									},
								)}
								{usageByType.total === 0 && (
									<div className="text-center py-8 text-sm text-muted-foreground">
										No usage data available yet
									</div>
								)}
							</TabsContent>
							<TabsContent value="endpoints" className="mt-4">
								{/* Mock endpoint data - will be replaced with real data from analytics API */}
								{(() => {
									const endpointData = [
										{ path: "/v1/text/generate", requests: 1234, errors: 12 },
										{ path: "/v1/image/generate", requests: 567, errors: 5 },
										{ path: "/v1/video/generate", requests: 89, errors: 2 },
										{ path: "/v1/audio/generate", requests: 234, errors: 8 },
										{ path: "/v1/models/list", requests: 456, errors: 0 },
										{ path: "/v1/providers/list", requests: 78, errors: 1 },
									];

									return (
										<div className="max-h-64 overflow-y-auto">
											<ResponsiveTable
												data={endpointData}
												keyExtractor={(item) => item.path}
												columns={[
													{
														key: "endpoint",
														header: "Endpoint",
														mobileLabel: "Endpoint",
														render: (item) => (
															<span className="font-mono text-xs">
																{item.path}
															</span>
														),
													},
													{
														key: "requests",
														header: "Requests",
														mobileLabel: "Requests",
														render: (item) => (
															<span className="text-sm">
																{item.requests.toLocaleString()}
															</span>
														),
													},
													{
														key: "errors",
														header: "Errors",
														mobileLabel: "Errors",
														render: (item) => (
															<span className="text-sm text-red-500">
																{item.errors}
															</span>
														),
													},
												]}
												emptyMessage="No endpoint data available"
											/>
										</div>
									);
								})()}
							</TabsContent>
						</Tabs>
					</CardContent>
				</Card>
			</div>

			{/* Admin Section - Only for admin/superAdmin */}
			{isAdmin && adminStats && (
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<AlertCircle className="w-5 h-5" />
							System Overview (Admin)
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-2 md:grid-cols-4 gap-4">
							<div className="p-4 rounded-lg bg-muted/50">
								<p className="text-sm text-muted-foreground">Total Users</p>
								<p className="text-2xl font-bold">
									{adminStats?.users?.total || 0}
								</p>
							</div>
							<div className="p-4 rounded-lg bg-muted/50">
								<p className="text-sm text-muted-foreground">Active Users</p>
								<p className="text-2xl font-bold">
									{adminStats?.users?.active || 0}
								</p>
							</div>
							<div className="p-4 rounded-lg bg-muted/50">
								<p className="text-sm text-muted-foreground">Projects</p>
								<p className="text-2xl font-bold">
									{adminStats?.projects?.total || 0}
								</p>
							</div>
							<div className="p-4 rounded-lg bg-muted/50">
								<p className="text-sm text-muted-foreground">
									Today's Requests
								</p>
								<p className="text-2xl font-bold">
									{adminStats?.usage?.todayRequests?.toLocaleString() || 0}
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}

function DashboardSkeleton({
	isAdmin,
}: {
	isAdmin: boolean;
}): React.JSX.Element {
	return (
		<div className="space-y-6">
			<div className="flex items-center justify-between">
				<div>
					<Skeleton className="h-8 w-64 mb-2" />
					<Skeleton className="h-4 w-96" />
				</div>
				<Skeleton className="h-10 w-36" />
			</div>

			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
				{[1, 2, 3, 4].map((i) => (
					<Card key={i}>
						<CardHeader className="pb-2">
							<Skeleton className="h-4 w-24" />
						</CardHeader>
						<CardContent>
							<Skeleton className="h-8 w-16 mb-1" />
							<Skeleton className="h-3 w-20" />
						</CardContent>
					</Card>
				))}
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				<Card className="lg:col-span-2">
					<CardHeader>
						<Skeleton className="h-6 w-32" />
					</CardHeader>
					<CardContent className="space-y-4">
						{[1, 2, 3].map((i) => (
							<Skeleton key={i} className="h-20 rounded-lg" />
						))}
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<Skeleton className="h-6 w-32" />
					</CardHeader>
					<CardContent className="space-y-4">
						{[1, 2, 3, 4].map((i) => (
							<div key={i} className="space-y-2">
								<Skeleton className="h-4 w-full" />
								<Skeleton className="h-2 w-full" />
							</div>
						))}
					</CardContent>
				</Card>
			</div>

			{isAdmin && (
				<Card>
					<CardHeader>
						<Skeleton className="h-6 w-48" />
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-4 gap-4">
							{[1, 2, 3, 4].map((i) => (
								<Skeleton key={i} className="h-20 rounded-lg" />
							))}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}
